import React from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from './home/Navbar';
import Footer from './Footer';
import { useNavigate } from 'react-router-dom';

function DemoTicketTimeAnalyser() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const features = t('demo_ticket_time_analyser.features', { returnObjects: true });
  const useCases = t('demo_ticket_time_analyser.use_cases', { returnObjects: true });
  const privacyFeatures = t('demo_ticket_time_analyser.privacy_features', { returnObjects: true });

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="flex flex-col min-h-screen bg-ink font-sans text-white antialiased">
      <Navbar />
      
      {/* Section 1: Demo GIF */}
      <section className="w-full bg-ink pt-28 pb-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white">{t('demo_ticket_time_analyser.title')}</h2>
          <div className="w-full rounded-2xl overflow-hidden border border-line shadow-2xl bg-surface flex items-center justify-center">
            <img
              src="/ticket_time_analyser_demo.gif"
              alt="Ticket Time Analyser Demo"
              className="w-full h-auto object-contain"
            />
          </div>
        </div>
      </section>

      {/* Section 2: App Functionality */}
      <section className="w-full bg-surface py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white">{t('demo_ticket_time_analyser.functionality_title')}</h2>
          
          <div className="space-y-8">
            <div className="bg-elevated border border-line p-8 rounded-xl">
              <p className="text-lg text-white/70 mb-6">
                {t('demo_ticket_time_analyser.description')}
              </p>

              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-white">{t('demo_ticket_time_analyser.what_it_does_title')}</h3>
                <ul className="space-y-3 text-white/70">
                  {features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-accent-400 mr-2">•</span>
                      <span dangerouslySetInnerHTML={{ __html: feature.replace(/^([^:]+):/, '<strong>$1:</strong>') }} />
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mb-8">
                <h3 className="text-xl font-semibold mb-4 text-white">{t('demo_ticket_time_analyser.use_cases_title')}</h3>
                <ul className="space-y-3 text-white/70">
                  {useCases.map((useCase, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-accent-400 mr-2">•</span>
                      {useCase}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Data Privacy & Security */}
      <section className="w-full bg-ink py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white">{t('demo_ticket_time_analyser.privacy_title')}</h2>
          
          <div className="bg-elevated border border-line p-8 rounded-xl">
            <p className="text-lg text-white/70 mb-6">
              {t('demo_ticket_time_analyser.privacy_description')}
            </p>

            <ul className="space-y-3 text-white/70 mb-6">
              {privacyFeatures.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-accent-400 mr-2">•</span>
                  <span dangerouslySetInnerHTML={{ __html: feature.replace(/^([^:]+:)/, '<strong>$1</strong>') }} />
                </li>
              ))}
            </ul>

            <p className="text-white/70 mb-6">
              {t('demo_ticket_time_analyser.privacy_policy_text')}
            </p>

            <div className="bg-surface border border-line p-6 rounded-lg">
              <p className="text-white/70">
                {t('demo_ticket_time_analyser.support_text')}{' '}
                <a href="mailto:contacto@easyappforz.com" className="text-accent-400 hover:underline">
                  contacto@easyappforz.com
                </a>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Pricing */}
      <section className="w-full bg-surface py-16">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-white">{t('demo_ticket_time_analyser.pricing_title')}</h2>
          <div className="bg-elevated border border-line p-8 rounded-2xl">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">{t('demo_ticket_time_analyser.free_trial')}</h3>
                <p className="text-white/70">{t('demo_ticket_time_analyser.then_price')}</p>
              </div>
              <div className="flex flex-col md:flex-row gap-6">
                <a 
                  href="https://www.zendesk.com.mx/marketplace/apps/support/1167820/ticket-time-analyser/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block w-full md:w-auto px-8 py-3 bg-cta text-ink font-semibold rounded-lg hover:bg-cta-hover transition-colors duration-300 text-center"
                >
                  {t('demo_ticket_time_analyser.install_button')}
                </a>
                <button 
                  onClick={() => navigate('/', { state: { scrollTo: 'contact' } })}
                  className="w-full md:w-auto px-8 py-3 border border-line text-white font-medium rounded-lg hover:bg-white/5 transition-colors duration-300"
                >
                  {t('demo_ticket_time_analyser.schedule_demo_button')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default DemoTicketTimeAnalyser;
